import {
  TileFontSizeVariant,
  tileWidthScaleForVariant,
} from '@/components/diagrams/components/Tile';
import { Node } from '@/components/diagrams/FlowChart';
import {
  GraphVizNodeComponentType,
  parseIconShape,
  parseTileEmphasis,
} from '@/modules/graphViz/graphVizNodeConfig';

import { EntityDiagram_NodeConfigurationFragment } from '../graphql/EntityDiagram.generated';

/**
 * Applies saved node configurations (positions, component type, icon shape)
 * to diagram nodes.
 *
 * For disposition diagrams, this function applies node configurations that
 * were saved for a specific grantor death scenario. The nodeConfigurations
 * should correspond to the current disposition order being viewed (e.g.,
 * when grantor A dies first vs when grantor B dies first).
 *
 * @param nodes - The diagram nodes to apply configurations to
 * @param nodeConfigurations - Saved configurations for specific nodes
 * @returns Updated nodes with applied configurations
 */
export function applyNodeConfigurations(
  nodes: Node[],
  nodeConfigurations: EntityDiagram_NodeConfigurationFragment[]
): Node[] {
  if (!nodeConfigurations || nodeConfigurations.length === 0) {
    return nodes;
  }

  const configMap = new Map(
    nodeConfigurations.map((config) => [config.nodeID, config])
  );

  return nodes.map((node) => {
    const config = configMap.get(node.id);
    if (!config) {
      return node;
    }

    const positioned = {
      ...node,
      position: {
        x: config.xPosition ?? node.position.x,
        y: config.yPosition ?? node.position.y,
      },
    };

    // Only tile nodes carry componentType / iconShape data
    if (node.type !== 'tile') return positioned;

    // If the tile's data.width is an auto/min/max object and the config
    // stores a non-default fontSize, scale min/max by the cumulative width
    // factor for that variant so the reloaded tile matches how it looked
    // when the user last saved it. See useBulkTileSizeChange for the write
    // path that this undoes.
    const existingDataWidth = (positioned.data as { width?: unknown }).width;
    const isAutoWidth =
      typeof existingDataWidth === 'object' &&
      existingDataWidth !== null &&
      'width' in existingDataWidth &&
      (existingDataWidth as { width: unknown }).width === 'auto';

    const scaledWidth =
      isAutoWidth && config.fontSize
        ? (() => {
            const scale = tileWidthScaleForVariant(
              config.fontSize as TileFontSizeVariant
            );
            const { minWidth, maxWidth } = existingDataWidth as {
              width: 'auto';
              minWidth: number;
              maxWidth: number;
            };
            return {
              width: 'auto' as const,
              minWidth: minWidth * scale,
              maxWidth: maxWidth * scale,
            };
          })()
        : undefined;

    // Explicit pixel dimensions are only present once a tile was resized via
    // NodeResizer; they take precedence over the auto-width object so the tile
    // reloads at the size the user set. Backend width/height are non-nullable
    // float64, so an unsaved tile comes back as 0 (not null) — treat 0 as "not
    // set" so existing diagrams keep their computed default and we never hand
    // React Flow a zero-size node (which freezes its measurement loop).
    const explicitWidth =
      typeof config.width === 'number' && config.width > 0
        ? config.width
        : undefined;
    const explicitHeight =
      typeof config.height === 'number' && config.height > 0
        ? config.height
        : undefined;
    const hasExplicitDimensions = explicitWidth !== undefined;

    // Type assertion is safe: we're only adding optional fields to existing
    // tile data, which preserves the Node union contract.
    return {
      ...positioned,
      // Set top-level dims too so gridLayout sizes the node before ReactFlow
      // measures it.
      ...(hasExplicitDimensions && {
        width: explicitWidth,
        height: explicitHeight,
      }),
      data: {
        ...positioned.data,
        ...(config.type && {
          componentType: config.type as GraphVizNodeComponentType,
        }),
        ...(config.iconShape && {
          iconShape: parseIconShape(config.iconShape),
        }),
        ...(config.tileEmphasis && {
          tileEmphasis: parseTileEmphasis(config.tileEmphasis),
        }),
        ...(config.fontSize && {
          fontSizeVariant: config.fontSize,
        }),
        ...(config.dimensionsFinalized && { dimensionsFinalized: true }),
        ...(scaledWidth && { width: scaledWidth }),
        ...(hasExplicitDimensions && {
          width: explicitWidth,
          height: explicitHeight,
          dimensionsFinalized: true,
        }),
        ...(typeof config.notes === 'string' && { notes: config.notes }),
        ...(config.entityDiagramConfig && {
          hideGroupContents:
            config.entityDiagramConfig.hideGroupContents ?? false,
        }),
      },
    } as Node;
  });
}
